import { createSlice } from "@reduxjs/toolkit";

import {
  addNewGateAccessData,
  deleteGateAccessEntry,
  fetchGateAccessListData,
  updateGateAccessEntry,
} from "../thunks/gateAccessThunks";

import type { GateAccess } from "../../utils/types/gateAccess";

interface GateAccessState {
  gateAccessList: GateAccess[];
  loading: boolean;
  error: string | null;
  isDataFetched: boolean;
}

const initialState: GateAccessState = {
  gateAccessList: [],
  loading: false,
  error: null,
  isDataFetched: false,
};

export const gateAccessSlice = createSlice({
  name: "gateAccess",
  initialState,
  reducers: {
    resetDataFetched: (state) => {
      state.isDataFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGateAccessListData.pending, (state) => {
        state.loading = true;
        state.isDataFetched = true;
        state.error = null;
      })
      .addCase(fetchGateAccessListData.fulfilled, (state, action) => {
        state.gateAccessList = action.payload || [];
        state.loading = false;
      })
      .addCase(fetchGateAccessListData.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isDataFetched = false;
        state.loading = false;
      });

    builder
      .addCase(addNewGateAccessData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewGateAccessData.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.gateAccessList = [action.payload, ...state.gateAccessList];
        }
      })
      .addCase(addNewGateAccessData.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Unknown error… Please try again";
      });

    builder
      .addCase(updateGateAccessEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGateAccessEntry.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.gateAccessList = state.gateAccessList.map((item) =>
            item.gateAccessId === action.payload?.gateAccessId
              ? { ...item, ...action.payload }
              : item
          );
        }
      })
      .addCase(updateGateAccessEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Unknown error… Please try again";
      });

    builder
      .addCase(deleteGateAccessEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGateAccessEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.gateAccessList = state.gateAccessList.filter(
          (item) => item.gateAccessId !== action.payload?.id
        );
      })
      .addCase(deleteGateAccessEntry.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });
  },
});

export const { resetDataFetched } = gateAccessSlice.actions;
export default gateAccessSlice.reducer;
